import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api/client';
import { useAuth } from './hooks/useAuth';
import { LoginScreen } from './components/LoginScreen';
import { IntakePage } from './pages/IntakePage';
import { IssueDetailPage } from './pages/IssueDetailPage';
import { IssueListPage } from './pages/IssueListPage';
import { DOCS_URL, MCP_URL } from './config';

const MOBILE_MQ = '(max-width: 768px), (max-height: 500px)';

function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(MOBILE_MQ).matches;
}

function useCounts() {
  const [counts, setCounts] = useState({ open: 0, total: 0 });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.list({ limit: 1 }).catch(() => null),
      api.list({ status: 'open', limit: 1 }).catch(() => null),
    ]).then(([all, open]) => {
      if (cancelled) return;
      setCounts({
        total: all?.total ?? 0,
        open: open?.total ?? 0,
      });
    });
    return () => { cancelled = true; };
  }, [location.pathname]);

  return counts;
}

function Shell({
  children,
  user,
  logout,
}: {
  children: React.ReactNode;
  user: { email: string };
  logout: () => void;
}) {
  const [panelOpen, setPanelOpen] = useState(() => !isMobile());

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = (e: MediaQueryListEvent) => setPanelOpen(!e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const close = useCallback(() => setPanelOpen(false), []);
  const toggle = useCallback(() => setPanelOpen((v) => !v), []);
  const counts = useCounts();

  return (
    <div className={`app-layout ${panelOpen ? 'panel-open' : 'panel-closed'}`}>
      <aside className="sidebar">
        <header className="sidebar-header">
          <div>
            <h1>ISSUES</h1>
            <div className="sidebar-subtitle">AGENTOS · arkturian</div>
          </div>
        </header>
        <div className="sidebar-content">
          <div className="section">
            <h3>Navigation</h3>
            <NavLink to="/" end className="nav-link" onClick={close}>
              <span>Intake</span>
              <span className="count">▶</span>
            </NavLink>
            <NavLink to="/list" className="nav-link" onClick={close}>
              <span>Liste</span>
              <span className="count">{counts.total}</span>
            </NavLink>
            <NavLink to="/list?status=open" className="nav-link" onClick={close}>
              <span>Offen</span>
              <span className="count">{counts.open}</span>
            </NavLink>
          </div>

          {(DOCS_URL || MCP_URL) && (
          <div className="section">
            <h3>External</h3>
            {DOCS_URL && (
            <a className="nav-link" href={DOCS_URL} target="_blank" rel="noreferrer">
              <span>OpenAPI Docs</span>
              <span className="count">↗</span>
            </a>
            )}
            {MCP_URL && (
            <a className="nav-link" href={MCP_URL} target="_blank" rel="noreferrer">
              <span>MCP Endpoint</span>
              <span className="count">↗</span>
            </a>
            )}
          </div>
          )}

          <div className="section">
            <h3>Spec</h3>
            <div className="muted mono" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
              Content-Post #810
              <br />
              issue-api · port 8200
              <br />
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              tenant · arkturian
            </div>
          </div>
        </div>
        <footer className="sidebar-footer">
          <span className="user-email" title={user.email}>{user.email}</span>
          <button className="logout-btn" onClick={logout}>logout</button>
        </footer>
      </aside>

      {!panelOpen && (
        <button className="mobile-menu-button" onClick={toggle} aria-label="Open sidebar">
          ☰
        </button>
      )}
      {panelOpen && <div className="mobile-backdrop" onClick={close} aria-hidden="true" />}

      <main className="main">
        <div className="main-inner">{children}</div>
      </main>
    </div>
  );
}

function ShellRoutes() {
  return (
    <Routes>
      <Route path="/" element={<IntakePage />} />
      <Route path="/list" element={<IssueListPage />} />
      <Route path="/i/:id" element={<IssueDetailPage />} />
      <Route path="/auth/google/callback" element={<IntakePage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="empty-state">
      404 — Route nicht gefunden. <Link to="/">zurück</Link>
    </div>
  );
}

export default function App() {
  const { user, loading, error, startGoogleLogin, loginEmail, logout } = useAuth();

  const safeUser = useMemo(() => user, [user]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span className="spinner" />
        <span className="muted mono" style={{ marginLeft: 12, letterSpacing: 2, fontSize: 11 }}>SESSION CHECK…</span>
      </div>
    );
  }

  if (!safeUser) {
    return <LoginScreen onLogin={startGoogleLogin} onEmailLogin={loginEmail} error={error} />;
  }

  return (
    <Shell user={safeUser} logout={logout}>
      <ShellRoutes />
    </Shell>
  );
}
